import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Star } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { presetsApi } from "../../api/presets";

const VALUES = [1, 2, 3, 4, 5];

const SIZES = {
  sm: { icon: 14, gap: "gap-0.5" },
  md: { icon: 18, gap: "gap-1" },
};

const FOCUS_RING =
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5500] focus-visible:ring-offset-2 focus-visible:ring-offset-[#050505]";

// `ratingAvg` is the plain mean the backend exposes publicly (the
// Bayesian-weighted `rating_score` used for "Best rated" sort is internal,
// sort-only -- see backend/routers/presets.py's `_public_doc` whitelist and
// task-19-brief.md). A plain mean can be adjusted incrementally without
// knowing every individual rating, which is what makes this optimistic
// guess exact -- not a guess dressed up as one -- whenever the server
// agrees on the same arithmetic. The real response overwrites it either way
// the moment it arrives, so a rounding disagreement never lasts.
function optimisticAggregate(ratingAvg, ratingCount, prevValue, nextValue) {
  const sum = ratingAvg * ratingCount;
  if (prevValue == null && nextValue != null) {
    const count = ratingCount + 1;
    return { rating_avg: (sum + nextValue) / count, rating_count: count };
  }
  if (prevValue != null && nextValue == null) {
    const count = ratingCount - 1;
    return { rating_avg: count > 0 ? (sum - prevValue) / count : 0, rating_count: count };
  }
  if (prevValue != null && nextValue != null && prevValue !== nextValue) {
    return {
      rating_avg: ratingCount > 0 ? (sum - prevValue + nextValue) / ratingCount : 0,
      rating_count: ratingCount,
    };
  }
  return { rating_avg: ratingAvg, rating_count: ratingCount };
}

/**
 * RatingStars -- a 1-5 star control with two jobs sharing one look.
 *
 * Read-only (`interactive` false, the default): shows the public
 * `ratingAvg` / `ratingCount` to anyone. This is what a catalogue card
 * renders -- no auth, no API calls, nothing clickable.
 *
 * Interactive (`interactive` true): a signed-in user can set, change, or
 * clear their own rating, with hover/keyboard preview, optimistic update,
 * and rollback on failure. Signed-out users see the exact same control
 * (never a disabled/greyed-out look -- see rating-ui-report.md for why)
 * but activating a star routes to /login instead of calling the API.
 * Clicking (or pressing Enter/Space/the matching number key on) the star
 * that already matches your rating clears it -- the only way to undo, so
 * every rated star's accessible label says "press to clear".
 *
 * Props:
 * - presetId: string -- required when `interactive` is true.
 * - ratingAvg: number -- default 0. Plain mean; 0 is only "no ratings" when
 *   paired with `ratingCount === 0` (a real 0.0 average can't occur since
 *   ratings are 1-5).
 * - ratingCount: number -- default 0.
 * - myRating: number|null -- default null. The caller's own rating; ignored
 *   when signed out (a visitor has no rating of their own to show).
 * - interactive: bool -- default false.
 * - size: "sm" | "md" -- default "md".
 * - compact: bool -- default false. Interactive, but sized for a catalogue
 *   row: one line, no "Your rating" label, and until the viewer has rated
 *   (or hovers) the stars show the public average, dimmed, instead of five
 *   empty ones. The save/error message goes in a tooltip and a live region.
 * - className: string -- merged onto the root element.
 * - onRatingChange({ rating_avg, rating_count, my_rating }) -- called with
 *   the server's authoritative numbers after every successful rate/clear,
 *   so a parent can update its own copy of the preset without RatingStars
 *   needing to know that shape. Not called on failure (the optimistic
 *   change is rolled back locally instead).
 */
export default function RatingStars({
  presetId,
  ratingAvg = 0,
  ratingCount = 0,
  myRating = null,
  interactive = false,
  compact = false,
  size = "md",
  className = "",
  onRatingChange,
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [avg, setAvg] = useState(ratingAvg);
  const [count, setCount] = useState(ratingCount);
  const [mine, setMine] = useState(myRating);
  const [preview, setPreview] = useState(null);
  const [status, setStatus] = useState(null); // { kind: "own" | "error", text }
  const btnRefs = useRef([]);
  const seqRef = useRef(0);
  const statusTimerRef = useRef(null);

  // Resync from the parent whenever the actual incoming numbers change --
  // a different kit, or a fresh fetch of the same one. This never fights an
  // in-flight optimistic update: that update's own resolution is guarded by
  // `seqRef` separately, and this effect only reacts to the *props*
  // changing, not to the local state this component sets on itself.
  useEffect(() => {
    setAvg(ratingAvg);
    setCount(ratingCount);
    setMine(myRating);
    setPreview(null);
    setStatus(null);
  }, [presetId, ratingAvg, ratingCount, myRating]);

  useEffect(() => () => clearTimeout(statusTimerRef.current), []);

  const { icon, gap } = SIZES[size] ?? SIZES.md;

  if (!interactive) {
    const displayValue = Math.round(Math.min(5, Math.max(0, avg)));
    const label =
      count > 0
        ? `Rated ${avg.toFixed(1)} out of 5 from ${count} rating${count === 1 ? "" : "s"}`
        : "No ratings yet";
    return (
      <div className={`inline-flex items-center gap-1.5 ${className}`} role="img" aria-label={label}>
        <div className={`inline-flex ${gap}`} aria-hidden="true">
          {VALUES.map((v) => (
            <Star
              key={v}
              size={icon}
              strokeWidth={1.5}
              className={v <= displayValue ? "text-[#FF5500]" : "text-white/20"}
              fill={v <= displayValue ? "currentColor" : "none"}
            />
          ))}
        </div>
        {/* Unrated: the outline stars say it on their own, so no text. The
            aria-label above still reads "No ratings yet". */}
        {count > 0 && (
          <span aria-hidden="true" className="font-mono text-xs text-[#A1A1AA]">
            {`${avg.toFixed(1)} (${count})`}
          </span>
        )}
      </div>
    );
  }

  const scheduleStatusClear = () => {
    clearTimeout(statusTimerRef.current);
    statusTimerRef.current = setTimeout(() => setStatus(null), 5000);
  };

  const commit = async (nextValue) => {
    const seq = ++seqRef.current;
    const prevAvg = avg;
    const prevCount = count;
    const prevMine = mine;
    const guess = optimisticAggregate(avg, count, mine, nextValue);
    setAvg(guess.rating_avg);
    setCount(guess.rating_count);
    setMine(nextValue);
    setStatus(null);

    try {
      const { data } =
        nextValue == null
          ? await presetsApi.removeRating(presetId)
          : await presetsApi.rate(presetId, nextValue);
      if (seq !== seqRef.current) return; // superseded by a later click
      setAvg(data.rating_avg);
      setCount(data.rating_count);
      setMine(data.my_rating);
      onRatingChange?.(data);
    } catch (err) {
      if (seq !== seqRef.current) return; // a newer click already moved on
      setAvg(prevAvg);
      setCount(prevCount);
      setMine(prevMine);
      if (err?.response?.status === 403) {
        setStatus({ kind: "own", text: err.response.data?.detail || "You cannot rate your own kit" });
      } else {
        setStatus({ kind: "error", text: "Couldn't save your rating. Try again." });
      }
      scheduleStatusClear();
    }
  };

  const handleSelect = (value) => {
    if (!user) {
      navigate("/login");
      return;
    }
    commit(mine === value ? null : value);
  };

  const focusAndSelect = (value) => {
    handleSelect(value);
    btnRefs.current[value - 1]?.focus();
  };

  const move = (delta) => {
    const currentIdx = mine ? mine - 1 : -1;
    const nextIdx = Math.min(4, Math.max(0, currentIdx + delta));
    if (nextIdx === currentIdx) return; // clamped at an edge -- never toggles off by repetition
    focusAndSelect(VALUES[nextIdx]);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowRight" || e.key === "ArrowUp") {
      e.preventDefault();
      move(1);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
      e.preventDefault();
      move(-1);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const focused = document.activeElement;
      const idx = btnRefs.current.indexOf(focused);
      if (idx !== -1) handleSelect(VALUES[idx]);
    } else if (!e.ctrlKey && !e.metaKey && !e.altKey && e.key >= "1" && e.key <= "5") {
      // Bare digit keys only -- Ctrl/Cmd/Alt+1..5 stay the browser's own
      // shortcuts (tab switching etc.), never hijacked here.
      e.preventDefault();
      focusAndSelect(Number(e.key));
    }
  };

  const displayValue = preview ?? mine ?? 0;
  // compact only: nothing of the viewer's own to show, so show the average.
  const showingAvg = compact && preview == null && mine == null;
  const starValue = showingAvg ? Math.round(Math.min(5, Math.max(0, avg))) : displayValue;
  const starOn = showingAvg ? "text-[#FF5500]/60" : "text-[#FF5500]";

  const starLabel = (v) => {
    const plural = v === 1 ? "" : "s";
    if (!user) return `Sign in to rate ${v} star${plural}`;
    if (mine === v) return `Your rating: ${v} star${plural}. Press to clear.`;
    return `Rate ${v} star${plural}`;
  };

  return (
    <div
      className={compact ? `inline-flex items-center ${className}` : `inline-flex flex-col gap-1 ${className}`}
      title={compact && status ? status.text : undefined}
    >
      <div className={compact ? "flex items-center gap-1.5" : "flex flex-wrap items-center gap-2"}>
        {!compact && (
          <span className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA]">
            Your rating
          </span>
        )}
        <div
          role="radiogroup"
          aria-label="Rate this kit"
          onKeyDown={handleKeyDown}
          className={`inline-flex ${gap}`}
        >
          {VALUES.map((v, i) => (
            <button
              key={v}
              ref={(el) => (btnRefs.current[i] = el)}
              type="button"
              role="radio"
              aria-checked={mine === v}
              tabIndex={(mine ? mine === v : v === 1) ? 0 : -1}
              onClick={() => handleSelect(v)}
              onMouseEnter={() => setPreview(v)}
              onMouseLeave={() => setPreview(null)}
              onFocus={() => setPreview(v)}
              onBlur={() => setPreview(null)}
              aria-label={starLabel(v)}
              className={`rounded p-0.5 ${FOCUS_RING}`}
            >
              <Star
                size={icon}
                strokeWidth={1.5}
                className={v <= starValue ? starOn : "text-white/30"}
                fill={v <= starValue ? "currentColor" : "none"}
              />
            </button>
          ))}
        </div>
        {(!compact || count > 0) && (
          // compact: phones have no room beside the genre/BPM, and the stars
          // already carry the average there.
          <span
            aria-hidden="true"
            className={`whitespace-nowrap font-mono text-xs text-[#A1A1AA] ${compact ? "hidden sm:inline" : ""}`}
          >
            {count > 0 ? `${avg.toFixed(1)} (${count})` : "No ratings yet"}
          </span>
        )}
      </div>
      <div aria-live="polite" className={compact ? "sr-only" : "min-h-[1rem]"}>
        {status && (
          <p className={`text-xs ${status.kind === "error" ? "text-red-400" : "text-[#A1A1AA]"}`}>
            {status.text}
          </p>
        )}
      </div>
    </div>
  );
}
